import { createStore } from '@xstate/store';
import type { GlobalRequirement } from '../types';

// ============================================
// Global Requirements Store - Verwaltet globale Requirements
// ============================================

const GLOBAL_REQUIREMENTS_LOCAL_STORAGE_KEY = 'roadmap_2026_global_requirements';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface GlobalRequirementsContext {
  requirements: GlobalRequirement[];
}

// Lade Requirements aus LocalStorage
function loadRequirementsFromLocalStorage(): GlobalRequirement[] {
  try {
    const saved = localStorage.getItem(GLOBAL_REQUIREMENTS_LOCAL_STORAGE_KEY);
    if (!saved) {
      return [];
    }

    const parsed = JSON.parse(saved) as GlobalRequirement[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
}

// Speichere Requirements in LocalStorage
function saveRequirementsToLocalStorage(requirements: GlobalRequirement[]): void {
  localStorage.setItem(GLOBAL_REQUIREMENTS_LOCAL_STORAGE_KEY, JSON.stringify(requirements));
}

const initialContext: GlobalRequirementsContext = {
  requirements: loadRequirementsFromLocalStorage(),
};

export const globalRequirementsStore = createStore({
  context: initialContext,

  on: {
    'globalRequirement.add': (ctx, event: { requirement: GlobalRequirement }) => {
      // Prüfe ob Text bereits existiert (Text ist der Identifier)
      if (ctx.requirements.some(r => r.text === event.requirement.text)) {
        console.warn(`[globalRequirementsStore] Requirement "${event.requirement.text}" already exists`);
        return ctx;
      }
      return {
        ...ctx,
        requirements: [...ctx.requirements, event.requirement],
      };
    },

    'globalRequirement.update': (ctx, event: { text: string; updates: Partial<GlobalRequirement> }) => {
      const reqIndex = ctx.requirements.findIndex(r => r.text === event.text);
      if (reqIndex === -1) {
        console.warn(`[globalRequirementsStore] Requirement "${event.text}" not found`);
        return ctx;
      }

      // Prüfe ob neuer Text bereits von einem anderen Requirement verwendet wird
      if (event.updates.text && event.updates.text !== event.text) {
        const existingWithText = ctx.requirements.find(
          r => r.text === event.updates.text && r.text !== event.text
        );
        if (existingWithText) {
          console.warn(`[globalRequirementsStore] Text "${event.updates.text}" is already used`);
          return ctx;
        }
      }

      const updatedRequirements = [...ctx.requirements];
      updatedRequirements[reqIndex] = {
        ...updatedRequirements[reqIndex],
        ...event.updates,
      };

      return {
        ...ctx,
        requirements: updatedRequirements,
      };
    },

    'globalRequirement.delete': (ctx, event: { text: string }) => {
      const reqIndex = ctx.requirements.findIndex(r => r.text === event.text);
      if (reqIndex === -1) {
        console.warn(`[globalRequirementsStore] Requirement "${event.text}" not found`);
        return ctx;
      }
      return {
        ...ctx,
        requirements: ctx.requirements.filter(r => r.text !== event.text),
      };
    },

    'globalRequirements.reset': () => ({
      requirements: [],
    }),

    'globalRequirements.import': (_ctx, event: { requirements: GlobalRequirement[] }) => ({
      requirements: event.requirements,
    }),
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastRequirementsHash = '';

function computeRequirementsHash(requirements: GlobalRequirement[]): string {
  return requirements.map(r => `${r.text}:${r.type}:${r.description}`).join('|');
}

// Subscribe to changes and persist
globalRequirementsStore.subscribe((snapshot) => {
  const currentHash = computeRequirementsHash(snapshot.context.requirements);

  if (currentHash === lastRequirementsHash) return;
  lastRequirementsHash = currentHash;

  // Debounced persistence
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveRequirementsToLocalStorage(snapshot.context.requirements);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getGlobalRequirementsArray(): GlobalRequirement[] {
  return globalRequirementsStore.getSnapshot().context.requirements;
}

export function getGlobalRequirementByText(text: string): GlobalRequirement | undefined {
  return globalRequirementsStore.getSnapshot().context.requirements.find(r => r.text === text);
}

export type GlobalRequirementsStore = typeof globalRequirementsStore;
